import React from 'react';
import type { AnyRouter } from '@tanstack/react-router';
import { matchRoutePattern } from './match-route';
import { isHydraRoute, type RouteOwnershipAccount } from './route-ownership';
import { accountHasFeatureFlag, type AccountFeatureFlag } from '@/services/auth/feature-flags';
import { accountStore } from '@/services/auth/store';
import { expectRumPageRender } from '@/services/rum';
import { useRumPageRendered } from '@/services/rum/use-page-rendered';

type PrefetchEntry = {
  pattern: string;
  flag: AccountFeatureFlag | null;
  importFn: () => Promise<{ default: React.ComponentType }>;
};

type LazyRouteOptions = {
  /**
   * The page's rollout flag. Prefetch only while it is on for the account: the
   * embedded app renders links to a migrated page for every account, and an
   * account whose flag is off must not download a page it can never render.
   * The flag is read per hover, so it turning on later is picked up without a
   * reload.
   */
  flag?: AccountFeatureFlag;
};

const prefetchRegistry: PrefetchEntry[] = [];

/**
 * Drop-in replacement for `React.lazy` that also registers the route for
 * hover-based prefetching. When a user hovers over an `<a>` whose `href`
 * matches `pattern`, the chunk is fetched in the background so navigation
 * is instant.
 *
 * Pass an array when links to the page exist under more than one URL shape.
 * A page migrated out of the embedded app is the usual case: the embedded app
 * still renders its own links at the legacy addresses, and hover prefetch keys
 * off the href it finds in the DOM, not off the address the router serves.
 *
 * Once the module resolves, subsequent renders use the resolved component
 * synchronously — completely bypassing React.lazy and Suspense.
 */
// oxlint-disable-next-line typescript/no-explicit-any -- React.ComponentType<unknown> is too strict for components with optional props
export function lazyRoute<T extends React.ComponentType<any>>(
  pattern: string | string[],
  factory: () => Promise<{ default: T }>,
  options: LazyRouteOptions = {},
): React.FunctionComponent<React.ComponentProps<T>> {
  let resolved: T | null = null;
  let pending: Promise<{ default: T }> | null = null;

  const load = () => {
    pending ??= factory()
      .then((m) => {
        resolved = m.default;
        return m;
      })
      .catch((error) => {
        pending = null;
        throw error;
      });
    return pending;
  };

  for (const routePattern of Array.isArray(pattern) ? pattern : [pattern]) {
    prefetchRegistry.push({ pattern: routePattern, flag: options.flag ?? null, importFn: load });
  }

  // Announced here rather than in the component below: this runs during the
  // suspended render, when the chunk is first requested, so the beacon knows a
  // page is on its way before it can commit. Hover prefetch calls `load()`
  // directly and never reaches this, and a route whose page is gated away
  // never renders `Lazy` at all — neither should hold the flush open.
  const Lazy = React.lazy(() => {
    expectRumPageRender();
    return load();
  });

  // Wrapper that renders the resolved component directly when available,
  // skipping React.lazy (and its microtask-delayed Suspense) entirely.
  function PrefetchedRoute(props: React.ComponentProps<T>) {
    useRumPageRendered();

    if (resolved != null) {
      return React.createElement(resolved, props);
    }
    return React.createElement(Lazy, props);
  }

  return PrefetchedRoute;
}

const prefetched = new Set<string>();

type RouteChunkRouter = Pick<AnyRouter, 'getMatchedRoutes' | 'loadRouteChunk'>;

let routeChunkRouter: RouteChunkRouter | null = null;

/**
 * Also warm the split component chunks of every route that matches a prefetched
 * path. `autoCodeSplitting` builds each route's component into its own chunk, and
 * the router only fetches it on navigation, so warming the `lazyRoute` page alone
 * still leaves a download after the click. Only the chunks load: no loader or
 * `beforeLoad` runs, unlike `router.preloadRoute`. Only for paths this app owns
 * for the account: the embedded app's route chunk would pull in the page it
 * forwards to, which the account's flags may keep it from rendering. Call once
 * at app startup.
 */
export function registerRouteChunkPrefetch(router: RouteChunkRouter) {
  routeChunkRouter = router;
}

function prefetchRouteChunks(pathname: string, account: RouteOwnershipAccount) {
  if (routeChunkRouter == null || !isHydraRoute(pathname, account)) return;
  for (const route of routeChunkRouter.getMatchedRoutes(pathname).matchedRoutes) {
    // A chunk that fails here fails again, and is reported, on navigation.
    routeChunkRouter.loadRouteChunk(route)?.catch(() => {});
  }
}

/**
 * Fetch the chunk for every lazy route whose pattern matches `pathname`, and
 * the matched routes' own chunks (see `registerRouteChunkPrefetch`).
 * Idempotent per pathname once something has been fetched for it; a failed
 * fetch, or a path whose only matches sit behind a flag that is off, is not
 * remembered, so a later attempt retries.
 */
export function prefetchForPath(pathname: string) {
  if (prefetched.has(pathname)) return;

  const account = accountStore.get();
  prefetchRouteChunks(pathname, account);

  const entries = prefetchRegistry.filter(
    ({ pattern, flag }) =>
      matchRoutePattern(pathname, pattern) && (flag == null || accountHasFeatureFlag(account, flag)),
  );
  if (entries.length === 0) return;

  prefetched.add(pathname);
  for (const { importFn } of entries) {
    void importFn().catch(() => {
      prefetched.delete(pathname);
    });
  }
}

/** Return the internal pathname from an anchor element, or null if not an internal link. */
export function getInternalPathname(anchor: HTMLAnchorElement): string | null {
  const href = anchor.getAttribute('href');
  if (href == null || !href.startsWith('/')) return null;
  return href.split(/[#?]/)[0];
}

/** Prefetch the route chunk for whichever link the user is hovering. */
function handleLinkHover(event_: MouseEvent) {
  // Capture-phase delegation reaches text nodes and the document itself, so the
  // target is only sometimes an element. `instanceof` is the runtime evidence
  // that makes `closest` — and its tag-name overload's `HTMLAnchorElement` —
  // available without asserting.
  if (!(event_.target instanceof Element)) return;

  const anchor = event_.target.closest('a');
  if (anchor == null) return;

  const pathname = getInternalPathname(anchor);
  if (pathname != null) {
    prefetchForPath(pathname);
  }
}

let installed = false;

/**
 * Install a global listener that prefetches lazy route chunks when the user
 * hovers over a link. Call once at app startup.
 */
export function installLinkPrefetching() {
  if (installed) return;
  installed = true;

  // pointerenter doesn't bubble, so capture: true lets us delegate from document.
  // Unlike mouseover it only fires once per element entry, avoiding redundant work
  // from child elements inside anchors.
  document.addEventListener('pointerenter', handleLinkHover, { capture: true, passive: true });
}

/** Reset all module state. Test-only — not for production use. */
export function resetPrefetchState() {
  prefetchRegistry.length = 0;
  prefetched.clear();
  routeChunkRouter = null;
  if (installed) {
    document.removeEventListener('pointerenter', handleLinkHover, { capture: true });
  }
  installed = false;
}
